import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { listAllShows, type AdminShowSummary } from '@/lib/api/admin'

type ShowsTab = 'all' | 'pending' | 'published' | 'rejected'

const tabs: { id: ShowsTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending review' },
  { id: 'published', label: 'Published' },
  { id: 'rejected', label: 'Rejected' },
]

export function AdminShows() {
  const [shows, setShows] = useState<AdminShowSummary[]>([])
  const [activeTab, setActiveTab] = useState<ShowsTab>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    listAllShows()
      .then((items) => {
        if (cancelled) return
        setShows(items)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load shows')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    if (activeTab === 'all') return shows
    return shows.filter((s) => s.status === activeTab)
  }, [shows, activeTab])

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aViews = a.totalViews ?? 0
      const bViews = b.totalViews ?? 0
      if (bViews !== aViews) return bViews - aViews
      return a.title.localeCompare(b.title)
    })
  }, [filtered])

  const maxViews = useMemo(
    () => Math.max(0, ...sorted.map((s) => (s.totalViews ?? 0))),
    [sorted],
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">All shows</h1>
          <p className="mt-1 text-sm text-gray-400">
            Analytics-focused view of all series on Binyuma TV, combining status, episodes, and views.
          </p>
        </div>
      </div>

      <div className="inline-flex rounded-full bg-white/5 p-1 text-xs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1 rounded-full transition-colors ${
              activeTab === tab.id ? 'bg-[#D4AF37] text-black' : 'text-gray-300 hover:bg-white/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-800 bg-black/40">
        <div className="grid grid-cols-[2fr_minmax(0,1.4fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,1.1fr)] gap-3 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500 border-b border-gray-800">
          <div>Show</div>
          <div>Creator</div>
          <div>Status</div>
          <div className="text-right">Episodes</div>
          <div className="text-right">Total views</div>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-400">Loading shows…</div>
        ) : sorted.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400">
            No shows found yet. Once creators start submitting and you approve content, it will appear here.
          </div>
        ) : (
          <div className="divide-y divide-gray-900">
            {sorted.map((show) => {
              const views = show.totalViews ?? 0
              const widthPercent =
                maxViews > 0 ? Math.max(8, Math.round((views / maxViews) * 100)) : 0

              return (
                <Link
                  key={show.id}
                  to={`/admin/shows/${show.id}`}
                  className="grid grid-cols-[2fr_minmax(0,1.4fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,1.1fr)] gap-3 px-4 py-3 text-sm items-center hover:bg-white/5 transition-colors"
                >
                  <div>
                    <p className="font-medium text-white hover:text-[#D4AF37]">{show.title}</p>
                    <p className="text-[11px] text-gray-500">
                      Added{' '}
                      {show.submittedAt
                        ? new Date(show.submittedAt).toLocaleDateString()
                        : '—'}
                    </p>
                  </div>
                  <div className="text-gray-300 truncate">{show.creator}</div>
                  <div className="text-xs capitalize text-gray-300">{show.status}</div>
                  <div className="text-right text-gray-300 text-xs">
                    {show.episodeCount != null ? show.episodeCount : '—'}
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    <div className="w-24 h-1.5 rounded-full bg-gray-800 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#D4AF37]"
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-300">
                      {views ? views.toLocaleString() : '—'}
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>

      {error && (
        <p className="text-[11px] text-red-300">
          {error}
        </p>
      )}
    </div>
  )
}

